/**
 * „Както вчера" (iter1076) — едно докосване записва последния ден с хранения
 * в днешния дневник. Ползва готовия `POST /api/meals/copy-meal` (по едно
 * извикване за всяко хранене) и се скрива само, когато няма какво да повтори.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { History, Loader2, Check } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["breakfast", "lunch", "dinner", "snack"];
const LABEL_BG = { breakfast: "Закуска", lunch: "Обяд", dinner: "Вечеря", snack: "Междинно" };

const dayLabel = (date) => {
  const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (date === y) return "вчера";
  const [, m, d] = date.split("-");
  return `${Number(d)}.${Number(m)}`;
};

export default function RepeatYesterdayCard({ onCopied }) {
  const { isDark } = useTheme();
  const [day, setDay] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/meals/history?days=3`);
      const today = new Date().toISOString().slice(0, 10);
      for (const d of (data?.days || []).filter((x) => x.date !== today)) {
        const types = TYPES.filter((t) => (d?.meals?.[t] || []).length);
        if (!types.length) continue;
        const entries = types.flatMap((t) => d.meals[t]);
        setDay({
          date: d.date,
          types,
          count: entries.length,
          kcal: Math.round(entries.reduce((s, e) => s + (e.calories || 0), 0)),
        });
        return;
      }
      setDay(null);
    } catch {
      setDay(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    if (!day || busy) return;
    setBusy(true);
    try {
      for (const t of day.types) {
        await axios.post(`${API}/meals/copy-meal`, { source_date: day.date, meal_type: t });
      }
      setDone(true);
      toast.success(`Записано за днес: ${day.count} ${day.count === 1 ? "запис" : "записа"} ✓`);
      window.dispatchEvent(new Event("meal-log-changed"));
      window.dispatchEvent(new Event("meal-added"));
      onCopied?.();
    } catch {
      toast.error("Записването не се получи. Опитайте отново.");
    } finally {
      setBusy(false);
    }
  };

  if (!day) return null;

  return (
    <div
      data-testid="repeat-yesterday-card"
      className={`flex items-center gap-3 rounded-2xl border px-3 py-2.5 ${
        isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]" : "bg-white border-gray-100 shadow-sm"
      }`}
    >
      <div className="w-9 h-9 shrink-0 rounded-xl grid place-items-center bg-gradient-to-br from-amber-400 to-orange-500">
        <History className="w-4 h-4 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
          Както вчера
        </div>
        <div
          className={`text-[11px] truncate ${isDark ? "text-slate-400" : "text-slate-500"}`}
          data-testid="repeat-yesterday-summary"
        >
          {day.types.map((t) => LABEL_BG[t]).join(", ")} · {day.kcal} kcal · {dayLabel(day.date)}
        </div>
      </div>
      <button
        type="button"
        onClick={copy}
        disabled={busy}
        data-testid="repeat-yesterday-btn"
        className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[11px] font-bold text-white transition-all disabled:opacity-60 ${
          done ? "bg-emerald-500" : "bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-md"
        }`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : done ? <Check className="w-3.5 h-3.5" /> : null}
        {busy ? "Записване…" : done ? "Записано" : "Запишете"}
      </button>
    </div>
  );
}
